import {DestroyRef, inject, signal, WritableSignal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {debounceTime, filter, firstValueFrom, merge, MonoTypeOperatorFunction, Observable, Subject} from "rxjs";
import {tap} from "rxjs/operators";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {promiseMedResolvere} from "felles-ng2/støttefunksjoner/promiser";
import {ingenOperasjon} from "felles-ng2/støttefunksjoner/observable";

const debug = initDebugger('AsynkArbeidPågårSignal')

export type AsynkArbeidPågårSignal = WritableSignal<boolean> & AsynkArbeidPågår

/** Eksponerer et signal som også implementerer {@link AsynkArbeidPågår}.
 *
 * Typisk bruk i kontroller-klasse ifbm promise:
 * ```
 * laster = asynkArbeidPågårSignal();
 * ...
 * metodeSomKallerServer(){
 *     await this.laster.utfør(async () =>
 *           this.felt = await etPromiseSomKallerServer();
 * }
 * ```
 *
 * Typisk bruk i kontroller-klasse ifbm rxjs observerbar:
 * ```
 * laster = asynkArbeidPågårSignal();
 * ...
 * metodeSomStartAbonnemang(){
 *      const arbeid = laster.start()
 *      enObserverbar.pipe(arbeid.avsluttVedLeveranse()).subscribe(...)
 * }
 * ```
 * eller for bruk i pipes der lengekjørende arbeid kan utføre multiple ganger (feks switchMap):
 * ```
 * laster = asynkArbeidPågårSignal();
 * ...
 * metodeSomStartAbonnemang(){
 *      enObserverbarSomLevererMultipleVerdier.pipe(
 *          switchMap(data => this.lasterData.startArbeidAvsluttVedLeveranse(metodeSomTarLangTid(data))),
 *          ....
 *          ).subscribe(...)
 * }
 * ```
 */
export function asynkArbeidPågårSignal(konfigurasjon?: AsynkArbeidPågårSignalKonfigurasjon): AsynkArbeidPågårSignal {
    const asynkArbeidPågår = new AsynkArbeidPågårImpl(konfigurasjon);
    const signal = asynkArbeidPågår.arbeider as WritableSignal<boolean> & AsynkArbeidPågår;

    signal.utfør = arbeid => asynkArbeidPågår.utfør(arbeid);
    signal.utførOgSvelgException = arbeid => asynkArbeidPågår.utførOgSvelgException(arbeid);
    signal.startArbeidAvsluttVedLeveranse = observerbar => asynkArbeidPågår.startArbeidAvsluttVedLeveranse(observerbar);
    signal.start = () => asynkArbeidPågår.start();
    signal.avsluttet = () => asynkArbeidPågår.avsluttet();

    return signal;
}

export function standardMinimumVarighetKonfig(destroyRef?: DestroyRef): AsynkArbeidPågårSignalKonfigurasjon {
    if (erNil(destroyRef)) destroyRef = inject(DestroyRef);
    return {
        minimumVarighet: {
            varighet: 500,
            destroyRef: destroyRef
        }
    }
}

export interface AsynkArbeidPågår {

    /** setter lagrer=true, utfører angitt arbeid, setter lagrer=false */
    utfør<T>(arbeid: () => Promise<T>): Promise<T>;

    /** setter lagrer=true, utfører angitt arbeid, setter lagrer=false.
     * Svelger exception. */
    utførOgSvelgException<T>(arbeid: () => Promise<T>): Promise<T>;

    /** Signaliserer at laste-arbeid har startet.
     * Når dette arbeidet er avsluttet kan avsluttet() på returnert objekt kalles for å markere
     * at arbeidet er fullført. */
    start(): AsynkArbeidPågårSignalSubArbeid;

    startArbeidAvsluttVedLeveranse<T>(observerbar: Observable<T>): Observable<T>;

    /** Signaliserer at laste-arbeidet er avsluttet. */
    avsluttet(): void;
}

class AsynkArbeidPågårImpl implements AsynkArbeidPågår {

    private tilstandsendring = new Subject<boolean>();

    readonly arbeider = signal(false);

    constructor(private konfigurasjon: AsynkArbeidPågårSignalKonfigurasjon = {}) {
        this.oppdaterArbeiderVedTilstandsendring()
    }

    async utfør(arbeid: () => Promise<any>): Promise<any> {
        const laster = this.start();
        try {
            return await arbeid();

        } catch (e) {
            debug('Exception i AsynkArbeidPågårSignal.utfør(..) kastet videre', e);
            throw e;

        } finally {
            laster.avsluttet();
        }
    }

    async utførOgSvelgException(arbeid: () => Promise<any>): Promise<any> {
        const laster = this.start();
        try {
            return await arbeid();

        } catch (e) {
            debug('Exception i AsynkArbeidPågårSignal.utførOgSvelgException(..) ignorert', e);

        } finally {
            laster.avsluttet();
        }
    }

    startArbeidAvsluttVedLeveranse<T>(observerbar: Observable<T>): Observable<T> {
        const arbeid = this.start();
        return observerbar.pipe(arbeid.avsluttVedLeveranse());
    }

    start(): AsynkArbeidPågårSignalSubArbeid {
        this.tilstandsendring.next(true);
        return new AsynkArbeidPågårSignalSubArbeid(this);
    }

    avsluttet() {
        this.tilstandsendring.next(false);
    }

    // private hjelpemetoder

    private oppdaterArbeiderVedTilstandsendring() {
        let teller = 0;
        merge(
          //når arbeid startet
          this.tilstandsendring.pipe(
            filter(aktivert => aktivert),
            tap(() => teller++),
            tap(() => debug(`aktivert - teller: ${teller}`)),
            filter(() => teller <= 1)),
          //når arbeid avsluttet
          this.tilstandsendring.pipe(
            filter(aktivert => !aktivert),
            tap(() => teller--),
            this.dersomMinimumVarighet(konfigurasjon => debounceTime(konfigurasjon.varighet)),
            tap(() => debug(`avsluttet - teller: ${teller}`)),
            filter(() => teller < 1)),
        )
          //strengt tatt bør det ikke være nødvendig å legget til destroy-trigger her - siden vårt subject ikke produserer verdier annet enn gjennom være next() kall
          //men dette vil sikre at event pågående forsinket 'arbeid utført' blir levert og at det ikke finnes noen gjenværende timeouts
          .pipe(this.dersomMinimumVarighet(konfigurasjon => takeUntilDestroyed(konfigurasjon.destroyRef)))
          .subscribe(aktivert => {
              debug(`setter arbeider=${aktivert}, teller: ${teller}`);
              this.arbeider.set(aktivert)
          })
    }

    private dersomMinimumVarighet<T>(pipeSteg: (konfigurasjon: MinimumVarighetKonfigurasjon) => MonoTypeOperatorFunction<T>): MonoTypeOperatorFunction<T> {
        return erIkkeNil(this.konfigurasjon.minimumVarighet) ? pipeSteg(this.konfigurasjon.minimumVarighet) : ingenOperasjon()
    }
}

export class AsynkArbeidPågårSignalSubArbeid {

    private _erAvsluttet = false;
    private _vedAvsluttet = promiseMedResolvere<void>();

    constructor(private readonly signal: AsynkArbeidPågårImpl) {
    }

    /** Sann dersom arbeidet er avsluttet */
    get erAvsluttet(): boolean {
        return this._erAvsluttet;
    }

    /** Promise som resolver når dette sub-arbeidet markeres som ferdig */
    get vedAvsluttet(): Promise<void> {
        return this._vedAvsluttet.promise;
    }

    /** Signaliserer at laste-arbeidet er avsluttet. */
    avsluttet(): void {
        if (!this._erAvsluttet) {
            this.signal.avsluttet();
            this._vedAvsluttet.resolve();
        }
        this._erAvsluttet = true;
    }

    /**
     * Markerer arbeidet som avsluttet ved første leveranse fra observerbar, for bruk ifbm rxjs.
     * Avslutter også arbeidet dersom observerbar leverer en error og når abonnent avslutter.
     * Eks:
     * const arbeid = laster.start()
     * enObserverbar.pipe(arbeid.avsluttVedLeveranse()).subscribe(...)
     */
    avsluttVedLeveranse<T>(): MonoTypeOperatorFunction<T> {
        return tap({
            next: () => this.avsluttet(),
            finalize: () => this.avsluttet() //sørger for å avslutte arbeidet også ved error/complete
        });
    }

    /**
     * Markerer arbeidet som avsluttet ved første leveranse fra observerbar som angitt betingelse returnerer sann for, for bruk ifbm rxjs.
     * Avslutter også arbeidet dersom observerbar leverer en error og når abonnent avslutter.
     * Eks:
     * const arbeid = laster.start()
     * enObserverbar.pipe(arbeid.avsluttVedLeveranse()).subscribe(...)
     */
    avsluttVedLeveranseDersom<T>(betingelse: (leveranse: T) => boolean): MonoTypeOperatorFunction<T> {
        return tap({
            next: leveranse => {
                if (betingelse(leveranse)) this.avsluttet()
            },
            finalize: () => this.avsluttet() //sørger for å avslutte arbeidet også ved error/complete
        });
    }
}

export interface AsynkArbeidPågårSignalKonfigurasjon {
    /** Angir minste tidsinterval i millisekunder signalet er aktivt (true) */
    minimumVarighet?: MinimumVarighetKonfigurasjon
}

/** Angir minste tidsinterval i millisekunder signalet er aktivt (true) */
export interface MinimumVarighetKonfigurasjon {
    /** Varighet i millisekunder */
    varighet: number;
    destroyRef: DestroyRef;
}

